"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrcadDsnTransformSheetSymbolGenerator = void 0;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const TransformSchematic_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformSchematic");
const TransformNetLabel_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformNetLabel");
const TransformProcessingResult_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformProcessingResult");
const TransformSchematicReference_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformSchematicReference");
const TransformModule_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformModule");
const TransformComponent_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformComponent");
const TransformComponentSymbol_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformComponentSymbol");
const TransformProjectModule_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformProjectModule");
const TransformProject_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformProject");
const TransformObject_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformObject");
const orcadLayers_1 = require("../../../orcadLayers");
const OrcadDsnSymbolUtils_1 = require("./OrcadDsnSymbolUtils");
const OrcadDsnUtils_1 = require("./OrcadDsnUtils");
const OrcadDsnBusUtils_1 = require("./OrcadDsnBusUtils");
const OrcadDsnUtils_2 = require("./OrcadDsnUtils");
const OrcadDsnPortOccurrenceUtils_1 = require("./OrcadDsnPortOccurrenceUtils");
const ModelText_1 = require("../../../../../ModelText");
const DEFAULT_SHEET_SYMBOL_WIDTH = 200;
const DEFAULT_SHEET_SYMBOL_HEIGHT = 100;
class OrcadDsnTransformSheetSymbolGenerator {
    constructor() {
        this.runsFor = [TransformSchematic_1.TransformSchematic];
    }
    *generate(parent, _rootContext) {
        const page = parent.hierarchyEntry.page;
        const busPositionLookup = (0, OrcadDsnBusUtils_1.buildBusPositionLookup)(page);
        const reportOccurrenceDiagnostic = (message) => {
            parent.context.hierarchyPortDiagnostics = parent.context.hierarchyPortDiagnostics ?? new Set();
            parent.context.hierarchyPortDiagnostics.add(message);
        };
        for (const drawnInst of page.DrawnInst ?? []) {
            if (!drawnInst.ContentsLibName && !drawnInst.ContentsViewName) {
                continue;
            }
            const drawnInstMeta = {
                'hierarchical-sheet-entry': { sheetSymbolDbId: drawnInst.DbId.toString() },
            };
            for (const portInst of drawnInst.PortInstScalar ?? []) {
                const pinPosition = [drawnInst.LocX + portInst.HotptX, drawnInst.LocY + portInst.HotptY];
                const occurrenceIdentity = (0, OrcadDsnPortOccurrenceUtils_1.parentBasePortOccurrenceIdentity)(parent.instance, drawnInst.DbId.toString(), portInst.Name, reportOccurrenceDiagnostic);
                const pinLabel = new TransformNetLabel_1.TransformNetLabel(parent, {
                    position: pinPosition,
                    originalLabel: portInst.Name,
                    labelType: 'hierarchical-sheet-entry',
                    strategyMetadata: {
                        ...drawnInstMeta,
                        ...(occurrenceIdentity ? { 'port-occurrence': occurrenceIdentity } : {}),
                    },
                });
                yield pinLabel;
            }
            for (const portInst of drawnInst.PortInstBus ?? []) {
                const pinPosition = [drawnInst.LocX + portInst.HotptX, drawnInst.LocY + portInst.HotptY];
                const occurrenceIdentity = (0, OrcadDsnPortOccurrenceUtils_1.parentBasePortOccurrenceIdentity)(parent.instance, drawnInst.DbId.toString(), portInst.Name, reportOccurrenceDiagnostic);
                const occurrenceBindings = (0, OrcadDsnPortOccurrenceUtils_1.parentPortOccurrenceBindings)(parent.instance, drawnInst.DbId.toString(), portInst.Name, portInst.Name, reportOccurrenceDiagnostic);
                const portMembers = this.portMembersFromBindings(portInst.Name, occurrenceBindings);
                yield new TransformNetLabel_1.TransformNetLabel(parent, {
                    position: pinPosition,
                    originalLabel: portInst.Name,
                    labelType: 'hierarchical-sheet-entry',
                    strategyMetadata: {
                        ...drawnInstMeta,
                        ...(occurrenceIdentity ? { 'port-occurrence': occurrenceIdentity } : {}),
                        'port-bus': { name: portInst.Name, members: portMembers },
                    },
                    dimensions: ['bus'],
                });
                yield* this.generateBusPortMemberLabels(pinPosition, portInst.Name, drawnInstMeta, busPositionLookup, parent, occurrenceBindings);
            }
            yield* this.generateSchRef(parent, drawnInst);
        }
        for (const partInst of page.PartInst ?? []) {
            if (!(0, OrcadDsnUtils_1.isHierarchicalBlockPlacement)(partInst)) {
                continue;
            }
            if ((0, OrcadDsnUtils_1.unresolvedBlockIsPhysicalPart)(parent.instance.children.some((child) => child.sheetSymbol === partInst), parent.hierarchyEntry.design, partInst)) {
                continue;
            }
            if (partInst.ContentsLibName.length === 0) {
                yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
                    type: 'orcad-dsn-sheet-reference-without-library',
                    message: `[sheet-reference-without-library] ${partInst.Reference}: hierarchical block names view "${partInst.ContentsViewName}" but no library file`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                });
            }
            const partInstMeta = {
                'hierarchical-sheet-entry': { sheetSymbolDbId: partInst.DbId.toString() },
            };
            for (const portInst of partInst.PortInstScalar ?? []) {
                const pinPosition = this.portInstancePosition(partInst, portInst);
                const occurrenceIdentity = (0, OrcadDsnPortOccurrenceUtils_1.parentBasePortOccurrenceIdentity)(parent.instance, partInst.DbId.toString(), portInst.Position, reportOccurrenceDiagnostic);
                yield new TransformNetLabel_1.TransformNetLabel(parent, {
                    position: pinPosition,
                    originalLabel: portInst.Name,
                    labelType: 'hierarchical-sheet-entry',
                    strategyMetadata: {
                        ...partInstMeta,
                        ...(occurrenceIdentity ? { 'port-occurrence': occurrenceIdentity } : {}),
                    },
                });
            }
            for (const portInst of partInst.PortInstBus ?? []) {
                const pinPosition = this.portInstancePosition(partInst, portInst);
                const occurrenceIdentity = (0, OrcadDsnPortOccurrenceUtils_1.parentBasePortOccurrenceIdentity)(parent.instance, partInst.DbId.toString(), portInst.Position, reportOccurrenceDiagnostic);
                const occurrenceBindings = (0, OrcadDsnPortOccurrenceUtils_1.parentPortOccurrenceBindings)(parent.instance, partInst.DbId.toString(), portInst.Position, portInst.Name, reportOccurrenceDiagnostic);
                const portMembers = this.portMembersFromBindings(portInst.Name, occurrenceBindings);
                yield new TransformNetLabel_1.TransformNetLabel(parent, {
                    position: pinPosition,
                    originalLabel: portInst.Name,
                    labelType: 'hierarchical-sheet-entry',
                    strategyMetadata: {
                        ...partInstMeta,
                        ...(occurrenceIdentity ? { 'port-occurrence': occurrenceIdentity } : {}),
                        'port-bus': { name: portInst.Name, members: portMembers },
                    },
                    dimensions: ['bus'],
                });
                yield* this.generateBusPortMemberLabels(pinPosition, portInst.Name, partInstMeta, busPositionLookup, parent, occurrenceBindings);
            }
            yield* this.generateSchRefFromPartInst(parent, partInst);
        }
    }
    portInstancePosition(partInst, portInst) {
        if (portInst.RecordedX !== undefined && portInst.RecordedY !== undefined) {
            return [portInst.RecordedX, portInst.RecordedY];
        }
        return [partInst.LocX + portInst.HotptX, partInst.LocY + portInst.HotptY];
    }
    *generateBusPortMemberLabels(pinPosition, busPortName, metadata, busPositionLookup, parent, occurrenceBindings) {
        const parentBus = busPositionLookup(`${pinPosition[0]},${pinPosition[1]}`);
        const memberBindings = (this.supportsOccurrenceMemberBinding(occurrenceBindings) &&
            (0, OrcadDsnPortOccurrenceUtils_1.coversCompleteDeclaredBusRange)(busPortName, occurrenceBindings)) ?
            occurrenceBindings
            : [];
        const portMembers = this.portMembersFromBindings(busPortName, memberBindings);
        if (portMembers.length === 0) {
            return;
        }
        const occurrenceSuffixes = memberBindings.map((binding) => binding.parentMemberName?.match(/(\d+)$/)?.[1]);
        const hasUniqueOccurrenceSuffixes = occurrenceSuffixes.length > 0 &&
            occurrenceSuffixes.every((suffix) => suffix !== undefined) &&
            new Set(occurrenceSuffixes).size === occurrenceSuffixes.length;
        const usePositionalDim = parentBus !== undefined &&
            (memberBindings.length > 0 ?
                hasUniqueOccurrenceSuffixes
                : !(0, OrcadDsnBusUtils_1.busPortAlignsWithParent)(portMembers, parentBus.members, parentBus.name));
        for (let i = 0; i < portMembers.length; i++) {
            const member = portMembers[i];
            const occurrenceBinding = (0, OrcadDsnPortOccurrenceUtils_1.occurrenceBindingForDeclaredBusMember)(occurrenceBindings, i, member.index, portMembers.length);
            const dimension = usePositionalDim ? (0, OrcadDsnBusUtils_1.busPositionalDimensionName)(i) : (0, OrcadDsnBusUtils_1.busDimensionName)(member);
            yield new TransformNetLabel_1.TransformNetLabel(parent, {
                position: pinPosition,
                originalLabel: member.name,
                labelType: 'hierarchical-sheet-entry',
                strategyMetadata: {
                    ...metadata,
                    'bus-member': { busName: busPortName, memberName: member.name, memberIndex: member.index },
                    ...(occurrenceBinding ? { 'port-occurrence': occurrenceBinding.identity } : {}),
                },
                dimensions: [dimension],
            });
        }
    }
    portMembersFromBindings(busPortName, bindings) {
        return (0, OrcadDsnPortOccurrenceUtils_1.busPortMembersFromOccurrenceBindings)(busPortName, this.supportsOccurrenceMemberBinding(bindings) ? bindings : []);
    }
    supportsOccurrenceMemberBinding(bindings) {
        const parts = bindings.map((binding) => binding.parentMemberName?.match(/^(.*?)(\d+)$/));
        if (parts.length === 0 ||
            parts.some((part) => !part) ||
            bindings.some((binding) => binding.identity.literalBitIndex === undefined)) {
            return false;
        }
        const prefixes = new Set(parts.map((part) => part?.[1].toUpperCase()));
        const suffixes = new Set(parts.map((part) => part?.[2]));
        return prefixes.size === 1 && suffixes.size === parts.length;
    }
    *generateSchRef(parent, drawnInst) {
        const symbolLayer = orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Symbol].id;
        const sheetWidth = drawnInst.X2 - drawnInst.X1;
        const sheetHeight = drawnInst.Y2 - drawnInst.Y1;
        const compName = `schref_${drawnInst.DbId}`;
        const instance = parent.instance;
        const moduleRef = `${instance.pathUniqueId}::schref::${drawnInst.DbId}`;
        const centerX = sheetWidth / 2;
        const centerY = sheetHeight / 2;
        const pins = [];
        for (const portInst of [...(drawnInst.PortInstScalar ?? []), ...(drawnInst.PortInstBus ?? [])]) {
            if (portInst.DbId === undefined) {
                continue;
            }
            const relX = portInst.HotptX - centerX;
            const relY = portInst.HotptY - centerY;
            const onLeftEdge = portInst.HotptX <= drawnInst.X1;
            const onRightEdge = portInst.HotptX >= drawnInst.X2;
            const onTopEdge = portInst.HotptY <= drawnInst.Y1;
            const onBottomEdge = portInst.HotptY >= drawnInst.Y2;
            const pinStubLength = 5;
            const stubEnd = onLeftEdge ? [relX - pinStubLength, relY] : [relX + pinStubLength, relY];
            let nameJustify = onLeftEdge ? [-1, 0] : [1, 0];
            let angle = 0;
            let labelPos = [relX, relY];
            const markerDepth = 8;
            const spacing = markerDepth + 3;
            if (onLeftEdge) {
                labelPos = [relX + spacing, relY];
                nameJustify = [-1, 0];
            }
            else if (onRightEdge) {
                labelPos = [relX - spacing, relY];
                nameJustify = [1, 0];
            }
            else if (onTopEdge) {
                angle = 90;
                labelPos = [relX, relY + spacing];
                nameJustify = [1, 0];
            }
            else if (onBottomEdge) {
                angle = 90;
                labelPos = [relX, relY - spacing];
                nameJustify = [-1, 0];
            }
            const edge = onLeftEdge ? 'left'
                : onRightEdge ? 'right'
                    : onTopEdge ? 'top'
                        : 'bottom';
            const markerPoints = this.getPortMarkerPoints(portInst.Type, relX, relY, edge, markerDepth, markerDepth / 2);
            const pinDrawings = [
                {
                    layers: [symbolLayer],
                    drawing: {
                        type: 'text',
                        ...(0, ModelText_1.encodeStrippedText)({ raw: portInst.Name, dialects: [ModelText_1.TextDialect.Orcad] }),
                        pos: labelPos,
                        angle,
                        justify: nameJustify,
                        size: 4,
                        thickness: 1,
                    },
                },
            ];
            if (markerPoints) {
                pinDrawings.push({
                    layers: [symbolLayer],
                    drawing: {
                        type: 'polygon',
                        points: markerPoints,
                        filled: true,
                        width: 0,
                    },
                });
            }
            pins.push({
                type: 'pin',
                ref: `${portInst.Name}#${portInst.DbId}`,
                name: portInst.Name,
                number: `${portInst.Name}#${portInst.DbId}`,
                start: [relX, relY],
                end: stubEnd,
                layer: symbolLayer,
                width: 1,
                drawings: pinDrawings,
            });
        }
        const symbolDrawings = [
            {
                layers: [symbolLayer],
                drawing: {
                    type: 'rect',
                    start: [-centerX, -centerY],
                    end: [centerX, centerY],
                    strokeWidth: 1,
                    filled: false,
                },
            },
        ];
        const component = new TransformComponent_1.TransformComponent(parent, {
            baseComponent: {
                name: compName,
                isVirtual: true,
            },
        });
        yield component;
        const symbol = new TransformComponentSymbol_1.TransformComponentSymbol(component, {
            ref: compName,
            name: compName,
            pins,
            drawings: symbolDrawings,
            metadata: {},
        });
        yield symbol;
        const drawProperties = {};
        const namedValues = {
            Reference: drawnInst.DeviceDesignator || drawnInst.Reference,
            Value: drawnInst.PartValue || drawnInst.ContentsViewName,
        };
        for (const userProp of drawnInst.UserProperty ?? []) {
            namedValues[userProp.Name] = userProp.Value;
        }
        const locX = drawnInst.LocX;
        const locY = drawnInst.LocY;
        for (const displayProp of drawnInst.DisplayProperty ?? []) {
            const namedValue = namedValues[displayProp.Name];
            if (namedValue === undefined) {
                continue;
            }
            const fontSize = (0, OrcadDsnSymbolUtils_1.scaleFont)(displayProp.Font);
            drawProperties[displayProp.Name] = {
                ...(0, ModelText_1.encodeStrippedText)({ raw: namedValue, dialects: [ModelText_1.TextDialect.Orcad] }),
                pos: [locX + displayProp.LocX, locY + displayProp.LocY],
                layer: symbolLayer,
                angle: displayProp.Rotation * 90,
                justify: [-1, -1],
                size: fontSize,
                thickness: 1,
                hidden: false,
                mirror: [false, false],
            };
        }
        const project = TransformObject_1.TransformObject.FindParent(parent, TransformProject_1.TransformProject);
        const projectModuleName = namedValues['Reference'] ?? drawnInst.Name;
        const projectModule = new TransformProjectModule_1.TransformProjectModule(project, {
            name: projectModuleName,
            component,
            hideInBom: true,
        });
        yield projectModule;
        const moduleCenter = [locX + centerX, locY + centerY];
        const transformModule = new TransformModule_1.TransformModule(parent, {
            ref: moduleRef,
            footprintRef: compName,
            center: moduleCenter,
            transform: { pos: [0, 0], angle: 0, mirrored: [false, false] },
            name: projectModuleName,
            fullModuleName: projectModuleName,
            drawProperties,
            metadata: {
                'orcad.drawnInst.dbId': drawnInst.DbId,
                'orcad.drawnInst.contentsViewName': drawnInst.ContentsViewName,
                'orcad.drawnInst.contentsLibName': drawnInst.ContentsLibName,
            },
        }, component, projectModule);
        yield transformModule;
        const context = parent.context;
        const childInstance = instance.children.find((child) => child.sheetSymbol && child.sheetSymbol.DbId === drawnInst.DbId);
        const targetSchematic = childInstance ? (context.instanceSchematicMap.get(childInstance) ?? null) : null;
        yield new TransformSchematicReference_1.TransformSchematicReference(parent, {
            layers: [symbolLayer],
            name: namedValues['Reference'] ?? drawnInst.Name,
            file: drawnInst.ContentsLibName || drawnInst.ContentsViewName,
            transformModule,
            targetSchematic,
        });
    }
    *generateSchRefFromPartInst(parent, partInst) {
        const design = parent.hierarchyEntry.design;
        let sheetWidth;
        let sheetHeight;
        const resolved = this.resolvePartInstBoundingBox(design, partInst);
        if (resolved) {
            sheetWidth = resolved.X2 - resolved.X1;
            sheetHeight = resolved.Y2 - resolved.Y1;
        }
        else {
            const allPorts = [...(partInst.PortInstScalar ?? []), ...(partInst.PortInstBus ?? [])];
            if (allPorts.length === 0) {
                yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
                    type: 'orcad-dsn-sheet-symbol-default-geometry',
                    message: `[sheet-symbol-default-geometry] ${partInst.Reference}: hierarchical block has neither a cached bounding box nor ports, so it is drawn at ${DEFAULT_SHEET_SYMBOL_WIDTH}x${DEFAULT_SHEET_SYMBOL_HEIGHT}`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                });
                sheetWidth = DEFAULT_SHEET_SYMBOL_WIDTH;
                sheetHeight = DEFAULT_SHEET_SYMBOL_HEIGHT;
            }
            else {
                const xs = allPorts.map((p) => p.HotptX);
                const ys = allPorts.map((p) => p.HotptY);
                const padding = 20;
                sheetWidth = Math.max(...xs) - Math.min(...xs) + padding * 2;
                sheetHeight = Math.max(...ys) - Math.min(...ys) + padding * 2;
            }
        }
        const symbolLayer = orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Symbol].id;
        const compName = `schref_${partInst.DbId}`;
        const instance = parent.instance;
        const moduleRef = `${instance.pathUniqueId}::schref::${partInst.DbId}`;
        const centerX = sheetWidth / 2;
        const centerY = sheetHeight / 2;
        const pins = [];
        for (const portInst of [...(partInst.PortInstScalar ?? []), ...(partInst.PortInstBus ?? [])]) {
            const relX = portInst.HotptX - centerX;
            const relY = portInst.HotptY - centerY;
            const onLeftEdge = portInst.HotptX <= 0;
            const onRightEdge = portInst.HotptX >= sheetWidth;
            const onTopEdge = portInst.HotptY <= 0;
            const pinStubLength = 5;
            const stubEnd = onLeftEdge ? [relX - pinStubLength, relY] : [relX + pinStubLength, relY];
            let nameJustify = onLeftEdge ? [-1, 0] : [1, 0];
            let angle = 0;
            let labelPos = [relX, relY];
            const markerDepth = 8;
            const spacing = markerDepth + 3;
            if (onLeftEdge) {
                labelPos = [relX + spacing, relY];
                nameJustify = [-1, 0];
            }
            else if (onRightEdge) {
                labelPos = [relX - spacing, relY];
                nameJustify = [1, 0];
            }
            else if (onTopEdge) {
                angle = 90;
                labelPos = [relX, relY + spacing];
                nameJustify = [1, 0];
            }
            else {
                angle = 90;
                labelPos = [relX, relY - spacing];
                nameJustify = [-1, 0];
            }
            const edge = onLeftEdge ? 'left'
                : onRightEdge ? 'right'
                    : onTopEdge ? 'top'
                        : 'bottom';
            const markerPoints = this.getPortMarkerPoints(portInst.Type, relX, relY, edge, markerDepth, markerDepth / 2);
            const pinDrawings = [
                {
                    layers: [symbolLayer],
                    drawing: {
                        type: 'text',
                        ...(0, ModelText_1.encodeStrippedText)({ raw: portInst.Name, dialects: [ModelText_1.TextDialect.Orcad] }),
                        pos: labelPos,
                        angle,
                        justify: nameJustify,
                        size: 4,
                        thickness: 1,
                    },
                },
            ];
            if (markerPoints) {
                pinDrawings.push({
                    layers: [symbolLayer],
                    drawing: {
                        type: 'polygon',
                        points: markerPoints,
                        filled: true,
                        width: 0,
                    },
                });
            }
            pins.push({
                type: 'pin',
                ref: `${portInst.Name}#${portInst.DbId}`,
                name: portInst.Name,
                number: `${portInst.Name}#${portInst.DbId}`,
                start: [relX, relY],
                end: stubEnd,
                layer: symbolLayer,
                width: 1,
                drawings: pinDrawings,
            });
        }
        const symbolDrawings = [
            {
                layers: [symbolLayer],
                drawing: {
                    type: 'rect',
                    start: [-centerX, -centerY],
                    end: [centerX, centerY],
                    strokeWidth: 1,
                    filled: false,
                },
            },
        ];
        const component = new TransformComponent_1.TransformComponent(parent, {
            baseComponent: {
                name: compName,
                isVirtual: true,
            },
        });
        yield component;
        const symbol = new TransformComponentSymbol_1.TransformComponentSymbol(component, {
            ref: compName,
            name: compName,
            pins,
            drawings: symbolDrawings,
            metadata: {},
        });
        yield symbol;
        const drawProperties = {};
        const namedValues = {
            Reference: partInst.DeviceDesignator || partInst.Reference,
            Value: partInst.PartValue || partInst.ContentsViewName,
        };
        for (const userProp of partInst.UserProperty ?? []) {
            namedValues[userProp.Name] = userProp.Value;
        }
        const locX = partInst.LocX;
        const locY = partInst.LocY;
        for (const displayProp of partInst.DisplayProperty ?? []) {
            const namedValue = namedValues[displayProp.Name];
            if (namedValue === undefined) {
                continue;
            }
            const fontSize = (0, OrcadDsnSymbolUtils_1.scaleFont)(displayProp.Font);
            drawProperties[displayProp.Name] = {
                ...(0, ModelText_1.encodeStrippedText)({ raw: namedValue, dialects: [ModelText_1.TextDialect.Orcad] }),
                pos: [locX + displayProp.LocX, locY + displayProp.LocY],
                layer: symbolLayer,
                angle: displayProp.Rotation * 90,
                justify: [-1, -1],
                size: fontSize,
                thickness: 1,
                hidden: false,
                mirror: [false, false],
            };
        }
        const project = TransformObject_1.TransformObject.FindParent(parent, TransformProject_1.TransformProject);
        const projectModuleName = namedValues['Reference'] ?? partInst.Name;
        const projectModule = new TransformProjectModule_1.TransformProjectModule(project, {
            name: projectModuleName,
            component,
            hideInBom: true,
        });
        yield projectModule;
        const moduleCenter = [locX + centerX, locY + centerY];
        const transformModule = new TransformModule_1.TransformModule(parent, {
            ref: moduleRef,
            footprintRef: compName,
            center: moduleCenter,
            transform: { pos: [0, 0], angle: 0, mirrored: [false, false] },
            name: projectModuleName,
            fullModuleName: projectModuleName,
            drawProperties,
            metadata: {
                'orcad.partInst.dbId': partInst.DbId,
                'orcad.partInst.contentsViewName': partInst.ContentsViewName,
                'orcad.partInst.contentsLibName': partInst.ContentsLibName,
            },
        }, component, projectModule);
        yield transformModule;
        const context = parent.context;
        const childInstance = instance.children.find((child) => child.sheetSymbol && child.sheetSymbol.DbId === partInst.DbId);
        const targetSchematic = childInstance ? (context.instanceSchematicMap.get(childInstance) ?? null) : null;
        yield new TransformSchematicReference_1.TransformSchematicReference(parent, {
            layers: [symbolLayer],
            name: namedValues['Reference'] ?? partInst.Name,
            file: partInst.ContentsLibName || partInst.ContentsViewName,
            transformModule,
            targetSchematic,
        });
    }
    resolvePartInstBoundingBox(design, partInst) {
        const cache = design.Cache;
        if (!cache) {
            return undefined;
        }
        const packageResolution = OrcadDsnUtils_2.OrcadDsnUtils.findBestPackageForPartInst(cache, partInst);
        if (!packageResolution) {
            return undefined;
        }
        const { pkg } = packageResolution;
        for (const libPart of pkg.LibPart) {
            for (const normalView of libPart.NormalView ?? []) {
                const ref = `${libPart.CellName}${normalView.Suffix}`;
                if (ref === partInst.GraphicName) {
                    return normalView.BoundingBox;
                }
            }
        }
        return undefined;
    }
    getPortMarkerPoints(portType, relX, relY, edge, depth, halfWidth) {
        let ix, iy, ax, ay;
        switch (edge) {
            case 'left':
                ix = 1;
                iy = 0;
                ax = 0;
                ay = 1;
                break;
            case 'right':
                ix = -1;
                iy = 0;
                ax = 0;
                ay = 1;
                break;
            case 'top':
                ix = 0;
                iy = 1;
                ax = 1;
                ay = 0;
                break;
            case 'bottom':
                ix = 0;
                iy = -1;
                ax = 1;
                ay = 0;
                break;
        }
        const d = depth;
        const hw = halfWidth;
        const tail = d * 0.5;
        switch (portType) {
            case 0:
                return [
                    [relX - hw * ax, relY - hw * ay],
                    [relX + tail * ix - hw * ax, relY + tail * iy - hw * ay],
                    [relX + d * ix, relY + d * iy],
                    [relX + tail * ix + hw * ax, relY + tail * iy + hw * ay],
                    [relX + hw * ax, relY + hw * ay],
                ];
            case 1:
                return [
                    [relX, relY],
                    [relX + tail * ix - hw * ax, relY + tail * iy - hw * ay],
                    [relX + d * ix, relY + d * iy],
                    [relX + tail * ix + hw * ax, relY + tail * iy + hw * ay],
                ];
            case 2:
                return [
                    [relX, relY],
                    [relX + tail * ix - hw * ax, relY + tail * iy - hw * ay],
                    [relX + d * ix - hw * ax, relY + d * iy - hw * ay],
                    [relX + d * ix + hw * ax, relY + d * iy + hw * ay],
                    [relX + tail * ix + hw * ax, relY + tail * iy + hw * ay],
                ];
            default:
                return null;
        }
    }
}
exports.OrcadDsnTransformSheetSymbolGenerator = OrcadDsnTransformSheetSymbolGenerator;
//# sourceMappingURL=OrcadDsnTransformSheetSymbolGenerator.js.map